import React, { useState } from 'react';
import { observer } from 'mobx-react';
import { Link } from 'react-router-dom';
import LoomEmbed from './LoomEmbed';
import { GRADUATION } from './trainingPlan';
import {
  PanelCard, PanelHeader, PanelTitle, ProgressDots, Dot, HeaderButton,
  PanelBody, SlotRow, SlotMark, SlotContent, SlotTitle, SlotMeta, SlotActions,
  PrimaryCta, SecondaryCta, DisclosureButton, DoneBanner, PanelFooter,
} from './TrainingPlanPanel.styles';

// Collapse state is per-user AND per-run: a stale collapse from an old organic
// run must not silently pre-collapse a manager-assigned refresher (the anchor
// keys a distinct run).
const collapseKey = (userId, assignedAt) => `training_plan_collapsed_${userId}_${assignedAt || 'organic'}`;

// ['A'] -> 'A'; ['A','B'] -> 'A and B'; ['A','B','C'] -> 'A, B, and C'
const formatTitleList = (titles) => {
  if (!titles?.length) return '';
  if (titles.length === 1) return titles[0];
  if (titles.length === 2) return `${titles[0]} and ${titles[1]}`;
  return `${titles.slice(0, -1).join(', ')}, and ${titles[titles.length - 1]}`;
};

// The dashboard's daily Training Plan card. Purely presentational over
// planState (see planState.js) — DashboardPage owns the launchers and the
// dismiss/complete persistence.
const TrainingPlanPanel = observer(({ planState, currentUser, company, onLaunch, onRecommendation, onDismiss }) => {
  const userId = currentUser?.userId;
  const runKey = collapseKey(userId, planState?.assignedAt);
  const [collapsed, setCollapsed] = useState(() => {
    if (planState?.resurfaced) return true;
    try { return localStorage.getItem(runKey) === '1'; } catch { return false; }
  });
  const [videoOpenFor, setVideoOpenFor] = useState(null);
  const [busySlotId, setBusySlotId] = useState(null);
  const [hideArmed, setHideArmed] = useState(false);

  if (!planState?.panelVisible) return null;

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      try { localStorage.setItem(runKey, prev ? '0' : '1'); } catch { /* cosmetic only */ }
      return !prev;
    });
  };

  const launch = async (row, launcher) => {
    if (busySlotId) return;
    setBusySlotId(row.slot.id);
    try {
      await onLaunch(row, launcher);
    } finally {
      setBusySlotId(null);
    }
  };

  const { completedDays, currentDayNumber, totalDays, todaySlots, todayComplete, bonusToday, planComplete } = planState;

  if (planComplete) {
    return (
      <PanelCard>
        <PanelHeader>
          <PanelTitle>{GRADUATION.title} {'\u{1F389}'}</PanelTitle>
          <HeaderButton onClick={onDismiss}>Finish</HeaderButton>
        </PanelHeader>
        <PanelBody>
          <DoneBanner>{GRADUATION.intro}</DoneBanner>
          {GRADUATION.recommendations.map((rec) => (
            <SlotRow key={rec.id}>
              <SlotMark>{rec.icon}</SlotMark>
              <SlotContent>
                <SlotTitle>{rec.title}</SlotTitle>
                <SlotMeta>{rec.body}</SlotMeta>
                {rec.launchers?.length > 0 && onRecommendation && (
                  <SlotActions>
                    {rec.launchers.map((l, i) => {
                      const Cta = l.secondary ? SecondaryCta : PrimaryCta;
                      return (
                        <Cta key={l.label} onClick={() => onRecommendation(l)}>{l.label}</Cta>
                      );
                    })}
                  </SlotActions>
                )}
              </SlotContent>
            </SlotRow>
          ))}
        </PanelBody>
      </PanelCard>
    );
  }

  const headerLabel = planState.resurfaced
    ? `Pick up where you left off: Day ${currentDayNumber} of ${totalDays}`
    : `Your Training Plan · Day ${currentDayNumber} of ${totalDays}`;

  return (
    <PanelCard data-testid="training-plan-panel">
      <PanelHeader>
        <PanelTitle>{headerLabel}</PanelTitle>
        <ProgressDots>
          {Array.from({ length: totalDays }, (_, i) => (
            <Dot key={i} $state={i < completedDays ? 'done' : i === completedDays && !todayComplete ? 'current' : 'todo'} />
          ))}
        </ProgressDots>
        <HeaderButton onClick={() => setHideArmed(true)} title="Hide the Training Plan">Hide</HeaderButton>
        <HeaderButton onClick={toggleCollapsed} title={collapsed ? 'Expand' : 'Collapse'}>
          {collapsed ? '▼' : '▲'}
        </HeaderButton>
      </PanelHeader>

      {/* Hiding is sticky (it persists to the user record), so confirm and
          point at the Settings re-entry before committing. */}
      {hideArmed && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginTop: '10px', fontFamily: 'Montserrat', fontSize: '13px', color: '#64748b' }}>
          <span>Hide your Training Plan? You can resume it any time from Settings.</span>
          <SecondaryCta onClick={() => { setHideArmed(false); onDismiss(); }}>Hide it</SecondaryCta>
          <PrimaryCta onClick={() => setHideArmed(false)}>Keep it</PrimaryCta>
        </div>
      )}

      {!collapsed && (
        <PanelBody>
          {todaySlots.map((row) => {
            const { slot } = row;
            const video = slot.videoKey ? company?.trainingPlanVideos?.[slot.videoKey] : null;
            const isBusy = busySlotId === slot.id;
            return (
              <SlotRow key={slot.id} $next={row.isNext} $locked={!row.available}>
                <SlotMark $done={row.done}>
                  {row.done ? '✓' : row.available ? '○' : '\u{1F512}'}
                </SlotMark>
                <SlotContent>
                  <SlotTitle $done={row.done}>
                    {slot.title}
                    {slot.estimatedMinutes ? ` · ~${slot.estimatedMinutes} min` : ''}
                  </SlotTitle>
                  {!row.available && row.lockedReason && <SlotMeta>{row.lockedReason}</SlotMeta>}
                  {row.available && !row.done && (
                    <SlotMeta>{slot.kind === 'review' ? slot.description : slot.objective}</SlotMeta>
                  )}
                  {row.done && row.doneInteractive && (
                    <SlotMeta>You handled a live interruption. Nice work!</SlotMeta>
                  )}
                  {row.available && !row.done && (
                    <SlotActions>
                      {slot.kind === 'review' ? (
                        <PrimaryCta disabled={isBusy} onClick={() => launch(row, null)}>
                          {slot.linkLabel || 'Open'} ↗
                        </PrimaryCta>
                      ) : (
                        row.availableLaunchers.map((launcher, i) => {
                          const Cta = i === 0 ? PrimaryCta : SecondaryCta;
                          return (
                            <Cta key={launcher.label} disabled={isBusy} onClick={() => launch(row, launcher)}>
                              {isBusy && i === 0 ? 'Loading...' : launcher.label}
                            </Cta>
                          );
                        })
                      )}
                      {video?.loomId && (
                        <DisclosureButton onClick={() => setVideoOpenFor(videoOpenFor === slot.id ? null : slot.id)}>
                          {videoOpenFor === slot.id ? 'Hide example' : `Watch an example${video.durationLabel ? ` (${video.durationLabel})` : ''}`}
                        </DisclosureButton>
                      )}
                    </SlotActions>
                  )}
                  {row.available && !row.done && videoOpenFor === slot.id && video?.loomId && (
                    <div style={{ marginTop: '10px', maxWidth: '480px' }}>
                      <LoomEmbed loomId={video.loomId} title={video.title || `${slot.title} example`} />
                    </div>
                  )}
                </SlotContent>
              </SlotRow>
            );
          })}

          {todayComplete && (
            <DoneBanner>
              That's Day {currentDayNumber} done{bonusToday > 0 ? `, plus ${bonusToday} bonus session${bonusToday === 1 ? '' : 's'}` : ''}.
              {completedDays < totalDays && (
                planState.tomorrowTitles?.length
                  ? ` Come back tomorrow for Day ${Math.min(completedDays + 1, totalDays)}: ${formatTitleList(planState.tomorrowTitles)}.`
                  : ' Come back tomorrow for the next one.'
              )}
            </DoneBanner>
          )}

          <PanelFooter>
            Brush up any time: <Link to="/company">your company's message</Link>
            {' · '}
            <Link to="/playbook/building-message/anatomy-sales-cycle">the sales cycle</Link>
          </PanelFooter>
        </PanelBody>
      )}
    </PanelCard>
  );
});

export default TrainingPlanPanel;
